/**
 * Workout History — preview list for Health dashboard, full screen, share & export.
 *
 * Exports:
 *   <WorkoutHistorySection />     — preview (5 latest) for HealthDashboard
 *   <WorkoutHistoryPage />        — /health/workout-history full screen
 *   SessionCard, SessionDetailSheet, ShareSessionCard helpers
 */
import { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Download,
  MoreHorizontal,
  Share2,
  Trophy,
  X,
} from "lucide-react";
import html2canvas from "html2canvas";
import { apiClient, API } from "../../services/api";
import { toast } from "sonner";

const MODULE_LABEL = {
  gym: "Gym",
  home: "Home",
  yoga: "Yoga",
  zumba: "Zumba",
};

// ──────────────────────────────────────────────────────────────
// Health-dashboard inline preview
// ──────────────────────────────────────────────────────────────
export default function WorkoutHistorySection() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    apiClient
      .get("/workout/history?limit=5")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);

  return (
    <section data-testid="workout-history-section">
      <div className="flex items-end justify-between mb-3">
        <h2
          className="font-display"
          style={{ fontSize: 24, fontWeight: 800, color: "#1A1A1A", lineHeight: 1.05 }}
        >
          Workout History
        </h2>
        <Link
          to="/health/workout-history"
          data-testid="history-see-all"
          style={{
            fontFamily: "Manrope",
            fontSize: 13,
            fontWeight: 600,
            color: "#6B6760",
          }}
        >
          See All
        </Link>
      </div>

      {rows === null ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-white rounded-[16px] border border-line2 animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyHistory />
      ) : (
        <div className="space-y-3">
          {rows.map((s) => (
            <SessionCard key={s.session_id} session={s} />
          ))}
        </div>
      )}
    </section>
  );
}

function EmptyHistory() {
  return (
    <div
      className="rounded-[16px] p-6 text-center"
      style={{ background: "#FFFFFF", border: "1px solid #E2E0DC" }}
      data-testid="history-empty"
    >
      <div
        className="font-barlow-cond"
        style={{ fontSize: 22, fontWeight: 800, color: "#1A1A1A" }}
      >
        No workout history yet
      </div>
      <p
        className="mt-1"
        style={{ fontFamily: "Manrope", fontSize: 13, color: "#6B6760" }}
      >
        Complete your first workout to see it here.
      </p>
      <Link
        to="/workouts"
        data-testid="history-empty-cta"
        className="inline-block mt-4"
        style={{
          background: "#00D4FF",
          color: "#000",
          padding: "10px 22px",
          borderRadius: 100,
          fontFamily: "Manrope",
          fontWeight: 700,
          fontSize: 14,
        }}
      >
        Start a Workout
      </Link>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// SessionCard — used in preview + full list
// ──────────────────────────────────────────────────────────────
export function SessionCard({ session }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const dt = session.completed_at || session.started_at || "";
  const dateObj = dt ? new Date(dt) : null;
  const dayLabel = dateObj
    ? dateObj.toLocaleDateString("en-US", { weekday: "short" })
    : "—";
  const dayNum = dateObj ? dateObj.getDate() : "—";
  const monthYear = dateObj
    ? dateObj.toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : "";

  const muscles = session.muscles_worked || [];
  const moduleLabel = MODULE_LABEL[session.module] || "Workout";

  return (
    <>
      <div
        data-testid={`session-card-${session.session_id}`}
        className="relative cursor-pointer"
        style={{
          background: "#FFFFFF",
          border: "1px solid #E2E0DC",
          borderRadius: 16,
          padding: "16px 20px",
        }}
        onClick={() => setSheetOpen(true)}
      >
        <div className="flex items-stretch gap-3">
          {/* Left — date */}
          <div className="text-center" style={{ minWidth: 48 }}>
            <div
              className="font-barlow-cond"
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#1A1A1A",
                lineHeight: 1,
              }}
            >
              {dayLabel} {dayNum}
            </div>
            <div
              style={{
                fontFamily: "Manrope",
                fontSize: 11,
                fontWeight: 500,
                color: "#8E8A84",
                marginTop: 4,
              }}
            >
              {monthYear}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            {/* Center — name + meta + muscles */}
            <div className="flex items-center gap-2 flex-wrap">
              <div
                className="font-barlow-cond truncate"
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#1A1A1A",
                  maxWidth: "100%",
                }}
              >
                {session.workout_name || "Workout"}
              </div>
              {session.contains_pr && (
                <span
                  data-testid={`session-pr-badge-${session.session_id}`}
                  className="inline-flex items-center gap-1"
                  style={{
                    background: "#00D4FF",
                    color: "#0B0B0B",
                    fontFamily: "Manrope",
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "2px 8px",
                    borderRadius: 100,
                    letterSpacing: "0.06em",
                  }}
                >
                  <Trophy size={10} strokeWidth={2.4} /> PR
                </span>
              )}
            </div>
            <div
              style={{
                fontFamily: "Manrope",
                fontSize: 12,
                fontWeight: 500,
                color: "#8E8A84",
                marginTop: 2,
              }}
            >
              {session.duration_minutes} min · {session.exercise_count}{" "}
              {session.exercise_count === 1 ? "exercise" : "exercises"} · {moduleLabel}
            </div>
            {muscles.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {muscles.slice(0, 3).map((m) => (
                  <span
                    key={m}
                    className="capitalize"
                    style={{
                      background: "#ECEAE7",
                      color: "#6B6760",
                      fontFamily: "Manrope",
                      fontSize: 10,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 100,
                    }}
                  >
                    {m}
                  </span>
                ))}
                {muscles.length > 3 && (
                  <span
                    style={{
                      fontFamily: "Manrope",
                      fontSize: 10,
                      fontWeight: 500,
                      color: "#8E8A84",
                      padding: "2px 4px",
                    }}
                  >
                    +{muscles.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Right — stats */}
          <div className="text-right shrink-0">
            <div className="flex items-baseline justify-end gap-1">
              <span
                className="font-barlow-cond"
                style={{ fontSize: 22, fontWeight: 900, color: "#1A1A1A" }}
              >
                {session.calories_burned || 0}
              </span>
              <span
                style={{
                  fontFamily: "Manrope",
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#00B0D5",
                }}
              >
                kcal
              </span>
            </div>
            <div
              style={{
                fontFamily: "Manrope",
                fontSize: 12,
                fontWeight: 500,
                color: "#8E8A84",
                marginTop: 2,
              }}
            >
              {session.total_volume_kg || 0} kg
            </div>
          </div>

          <button
            data-testid={`session-menu-${session.session_id}`}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="self-start -mr-2 -mt-1 w-8 h-8 grid place-items-center rounded-full hover:bg-canvas2"
            aria-label="Card menu"
          >
            <MoreHorizontal size={16} color="#6B6760" />
          </button>
        </div>

        {menuOpen && (
          <div
            className="absolute right-3 top-12 z-10"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#FFFFFF",
              border: "1px solid #E2E0DC",
              borderRadius: 12,
              boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              minWidth: 160,
            }}
          >
            <button
              data-testid={`session-share-${session.session_id}`}
              onClick={() => {
                setMenuOpen(false);
                setShareOpen(true);
              }}
              className="w-full text-left px-4 py-3 flex items-center gap-2 hover:bg-canvas2"
              style={{
                fontFamily: "Manrope",
                fontSize: 13,
                fontWeight: 600,
                color: "#1A1A1A",
              }}
            >
              <Share2 size={14} /> Share Session
            </button>
          </div>
        )}
      </div>

      <SessionDetailSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        sessionId={session.session_id}
        seed={session}
        onShare={() => {
          setSheetOpen(false);
          setShareOpen(true);
        }}
      />

      <ShareSessionModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        session={session}
      />
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Detail bottom sheet
// ──────────────────────────────────────────────────────────────
export function SessionDetailSheet({ open, onClose, sessionId, seed, onShare }) {
  const [detail, setDetail] = useState(seed);

  useEffect(() => {
    if (!open || !sessionId) return;
    apiClient
      .get(`/workout/history/${sessionId}`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(seed));
  }, [open, sessionId]);

  if (!open) return null;

  const dt = detail?.completed_at || detail?.started_at || "";
  const dateLabel = dt
    ? new Date(dt).toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      data-testid="session-detail-sheet"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.22 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-t-[24px] bg-white"
        style={{
          maxHeight: "86vh",
          overflow: "hidden",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="px-5 pt-4 pb-3 flex items-start justify-between border-b border-line2">
          <div>
            <h3
              className="font-barlow-cond"
              style={{ fontSize: 24, fontWeight: 800, color: "#1A1A1A" }}
            >
              {detail?.workout_name || "Workout"}
            </h3>
            <p
              style={{
                fontFamily: "Manrope",
                fontSize: 12,
                fontWeight: 500,
                color: "#8E8A84",
                marginTop: 2,
              }}
            >
              {dateLabel} · {detail?.duration_minutes || 0} min
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="detail-close"
            aria-label="Close"
            className="w-9 h-9 grid place-items-center rounded-full bg-canvas2"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto" style={{ maxHeight: "60vh" }}>
          {(detail?.exercises || []).map((e, i) => (
            <div
              key={i}
              className="py-3 flex items-start justify-between gap-3"
              style={{ borderBottom: "1px solid #EEECE9" }}
              data-testid={`detail-exercise-${i}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div
                    className="truncate"
                    style={{
                      fontFamily: "Manrope",
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#1A1A1A",
                    }}
                  >
                    {e.name || "Exercise"}
                  </div>
                  {e.pr_this_session && (
                    <span
                      className="inline-flex items-center gap-1"
                      data-testid={`detail-pr-${i}`}
                      style={{
                        background: "#00D4FF",
                        color: "#0B0B0B",
                        fontFamily: "Manrope",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "2px 8px",
                        borderRadius: 100,
                      }}
                    >
                      <Trophy size={10} strokeWidth={2.4} /> New PR
                    </span>
                  )}
                </div>
                {e.muscle_group && (
                  <div
                    className="capitalize"
                    style={{
                      fontFamily: "Manrope",
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#8E8A84",
                      marginTop: 2,
                    }}
                  >
                    {e.muscle_group}
                  </div>
                )}
              </div>
              <div
                className="text-right whitespace-nowrap"
                style={{
                  fontFamily: "Manrope",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#6B6760",
                }}
              >
                {e.sets || 0} × {e.reps || 0}
                {e.weight_kg ? ` × ${e.weight_kg} kg` : ""}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 flex items-center justify-between border-t border-line2 bg-canvas2">
          <div>
            <div
              style={{
                fontFamily: "Manrope",
                fontSize: 11,
                fontWeight: 700,
                color: "#8E8A84",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Total
            </div>
            <div
              style={{
                fontFamily: "Manrope",
                fontSize: 13,
                fontWeight: 600,
                color: "#1A1A1A",
                marginTop: 2,
              }}
            >
              {detail?.duration_minutes || 0} min · {detail?.calories_burned || 0} kcal · {detail?.total_volume_kg || 0} kg
            </div>
          </div>
          <button
            data-testid="detail-share"
            onClick={onShare}
            className="inline-flex items-center gap-1.5"
            style={{
              background: "#00D4FF",
              color: "#000",
              padding: "10px 18px",
              borderRadius: 100,
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            <Share2 size={14} /> Share
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Share modal — renders share card, html2canvas → PNG → native share
// ──────────────────────────────────────────────────────────────
function ShareSessionModal({ open, onClose, session }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleShare = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0A0A0A",
        scale: 2,
        useCORS: true,
      });
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error("Could not generate image");
          setBusy(false);
          return;
        }
        const file = new File(
          [blob],
          `fitfrik-${session.session_id || "workout"}.png`,
          { type: "image/png" }
        );
        if (
          navigator.canShare &&
          navigator.canShare({ files: [file] })
        ) {
          try {
            await navigator.share({
              files: [file],
              title: "FitFrik Workout",
              text: `${session.workout_name} on FitFrik`,
            });
          } catch {
            /* user cancelled */
          }
        } else {
          // Fallback — download
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.name;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
          toast.success("Image saved — share it from your gallery");
        }
        setBusy(false);
      }, "image/png");
    } catch (e) {
      setBusy(false);
      toast.error("Could not generate share image");
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center px-5"
      style={{ background: "rgba(0,0,0,0.65)" }}
      data-testid="share-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <ShareSessionCard ref={cardRef} session={session} />
        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 h-11"
            style={{
              background: "transparent",
              color: "#FFFFFF",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 100,
              fontFamily: "Manrope",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            data-testid="share-confirm"
            disabled={busy}
            className="flex-1 h-11 disabled:opacity-50"
            style={{
              background: "#00D4FF",
              color: "#000",
              borderRadius: 100,
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {busy ? "Generating…" : "Share"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Visual share card (forwardRef so html2canvas can capture it)
const ShareSessionCard = forwardRef(function ShareSessionCard({ session }, ref) {
  const dt = session.completed_at || session.started_at || "";
  const dateLabel = dt
    ? new Date(dt).toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
    : "";
  return (
    <div
      ref={ref}
      style={{
        width: "100%",
        background: "#0B0B0B",
        borderRadius: 22,
        padding: 28,
        color: "#F4F3F1",
        fontFamily: "Manrope",
      }}
    >
      <div className="flex items-center gap-2">
        <span
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 800,
            fontSize: 28,
            letterSpacing: "-1px",
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "baseline",
          }}
        >
          <span style={{ color: "#00D4FF" }}>F</span>
          <span style={{ color: "#F4F3F1" }}>itFrik</span>
        </span>
      </div>
      <div
        style={{
          fontFamily: "Manrope",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.1em",
          color: "#8E8A84",
          textTransform: "uppercase",
          marginTop: 22,
        }}
      >
        {dateLabel}
      </div>
      <h2
        style={{
          fontFamily: "Manrope",
          fontWeight: 800,
          fontSize: 30,
          lineHeight: 1.05,
          color: "#F4F3F1",
          marginTop: 6,
        }}
      >
        {session.workout_name || "Workout"}
      </h2>
      {session.contains_pr && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "#00D4FF",
            color: "#000",
            padding: "5px 12px",
            borderRadius: 100,
            marginTop: 12,
            fontFamily: "Manrope",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.06em",
          }}
        >
          NEW PERSONAL RECORD
        </div>
      )}

      <div
        className="grid grid-cols-2"
        style={{
          marginTop: 26,
          gap: 18,
          rowGap: 18,
        }}
      >
        <ShareStat label="Duration" value={`${session.duration_minutes || 0} min`} />
        <ShareStat label="Calories" value={`${session.calories_burned || 0} kcal`} />
        <ShareStat label="Volume" value={`${session.total_volume_kg || 0} kg`} />
        <ShareStat label="Exercises" value={session.exercise_count || 0} />
      </div>

      {(session.muscles_worked || []).length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div
            style={{
              fontFamily: "Manrope",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.1em",
              color: "#8E8A84",
              textTransform: "uppercase",
            }}
          >
            Muscles worked
          </div>
          <div className="flex flex-wrap" style={{ gap: 6, marginTop: 8 }}>
            {(session.muscles_worked || []).slice(0, 5).map((m) => (
              <span
                key={m}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  color: "#F4F3F1",
                  fontFamily: "Manrope",
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "4px 10px",
                  borderRadius: 100,
                  textTransform: "capitalize",
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 22,
          fontFamily: "Manrope",
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.1em",
          color: "#8E8A84",
          textTransform: "uppercase",
        }}
      >
        Trained on FitFrik
      </div>
    </div>
  );
});

function ShareStat({ label, value }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "Manrope",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.1em",
          color: "#8E8A84",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "Manrope",
          fontWeight: 800,
          fontSize: 22,
          color: "#F4F3F1",
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// re-export the share card as named
export { ShareSessionCard };

// ──────────────────────────────────────────────────────────────
// Full /health/workout-history page
// ──────────────────────────────────────────────────────────────
const FILTERS = [
  { id: "all", label: "All" },
  { id: "gym", label: "Gym" },
  { id: "home", label: "Home" },
  { id: "yoga", label: "Yoga" },
  { id: "zumba", label: "Zumba" },
];

export function WorkoutHistoryPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [sessions, setSessions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  // Reset pagination on filter change
  useEffect(() => {
    setPage(1);
    setSessions([]);
    setHasMore(false);
    loadPage(1, filter, true);
  }, [filter]);

  // Summary once
  useEffect(() => {
    apiClient
      .get("/workout/history/summary")
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null));
  }, []);

  const loadPage = async (pg, mod, replace = false) => {
    setLoading(true);
    try {
      const params = { page: pg, size: 20 };
      if (mod !== "all") params.module = mod;
      const { data } = await apiClient.get("/workout/history/full", { params });
      setSessions((prev) => (replace ? data.sessions : [...prev, ...data.sessions]));
      setHasMore(data.has_more);
      setPage(pg);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const r = await apiClient.get("/workout/history/export?days=90", {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "fitfrik_workout_history_90d.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Workout history exported");
    } catch {
      toast.error("Could not export. Try again.");
    }
  };

  // Group sessions by month-year
  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of sessions) {
      const dt = s.completed_at || s.started_at || "";
      const d = dt ? new Date(dt) : new Date();
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const label = d.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      if (!map.has(key)) map.set(key, { key, label, items: [] });
      map.get(key).items.push(s);
    }
    return Array.from(map.values());
  }, [sessions]);

  return (
    <div className="min-h-screen" style={{ background: "#F4F3F1" }} data-testid="workout-history-page">
      {/* Header */}
      <header
        className="sticky top-0 z-20"
        style={{
          background: "#F4F3F1",
          borderBottom: "1px solid #E2E0DC",
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        <div className="max-w-2xl mx-auto h-14 px-4 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            data-testid="history-back"
            aria-label="Back"
            className="-ml-2 w-10 h-10 grid place-items-center rounded-full hover:bg-white/60"
          >
            <ChevronLeft size={22} />
          </button>
          <h1
            className="font-barlow-cond"
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#1A1A1A",
              letterSpacing: "0.01em",
            }}
          >
            Workout History
          </h1>
          <div className="ml-auto">
            <button
              onClick={exportCsv}
              data-testid="history-export"
              className="inline-flex items-center gap-1.5"
              style={{
                background: "transparent",
                color: "#1A1A1A",
                border: "1px solid #C7C4BF",
                padding: "7px 14px",
                borderRadius: 100,
                fontFamily: "Manrope",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Download size={13} /> Export
            </button>
          </div>
        </div>

        {/* Filter pills */}
        <div className="max-w-2xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto" data-testid="history-filters">
          {FILTERS.map((f) => {
            const on = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className="shrink-0"
                style={{
                  background: on ? "#00D4FF" : "#ECEAE7",
                  color: on ? "#0B0B0B" : "#6B6760",
                  border: on ? "1px solid #00D4FF" : "1px solid #E2E0DC",
                  padding: "0 14px",
                  height: 32,
                  borderRadius: 100,
                  fontFamily: "Manrope",
                  fontSize: 12,
                  fontWeight: on ? 700 : 600,
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4 pb-10 space-y-5">
        {/* Summary strip */}
        {summary && (
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-2"
            data-testid="history-summary"
          >
            <SummaryTile
              label="Sessions"
              value={summary.total_sessions}
              testid="summary-sessions"
            />
            <SummaryTile
              label="Volume"
              value={`${formatVolume(summary.total_volume_kg)}`}
              suffix="kg"
              testid="summary-volume"
            />
            <SummaryTile
              label="Best Streak"
              value={summary.longest_streak_days}
              suffix="days"
              testid="summary-streak"
            />
            <SummaryTile
              label="PRs Set"
              value={summary.total_prs}
              testid="summary-prs"
            />
          </div>
        )}

        {/* Grouped sessions */}
        {sessions.length === 0 && !loading ? (
          <EmptyHistory />
        ) : (
          grouped.map((g) => (
            <section key={g.key} data-testid={`history-month-${g.key}`}>
              <div className="flex items-end justify-between mb-2">
                <div
                  className="font-barlow-cond"
                  style={{ fontSize: 18, fontWeight: 700, color: "#6B6760" }}
                >
                  {g.label}
                </div>
                <div
                  style={{
                    fontFamily: "Manrope",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#8E8A84",
                  }}
                >
                  {g.items.length} {g.items.length === 1 ? "session" : "sessions"}
                </div>
              </div>
              <div className="space-y-3">
                {g.items.map((s) => (
                  <SessionCard key={s.session_id} session={s} />
                ))}
              </div>
            </section>
          ))
        )}

        {hasMore && (
          <div className="text-center pt-2">
            <button
              data-testid="history-load-more"
              onClick={() => loadPage(page + 1, filter)}
              disabled={loading}
              style={{
                background: "transparent",
                color: "#1A1A1A",
                border: "1px solid #C7C4BF",
                padding: "10px 22px",
                borderRadius: 100,
                fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {loading ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function SummaryTile({ label, value, suffix, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E0DC",
        borderRadius: 14,
        padding: "12px 14px",
      }}
    >
      <div
        className="font-barlow-cond"
        style={{ fontSize: 22, fontWeight: 900, color: "#1A1A1A", lineHeight: 1 }}
      >
        {value}
        {suffix && (
          <span
            style={{
              fontFamily: "Manrope",
              fontSize: 11,
              fontWeight: 500,
              color: "#8E8A84",
              marginLeft: 6,
              display: "inline-block",
            }}
          >
            {suffix}
          </span>
        )}
      </div>
      <div
        style={{
          fontFamily: "Manrope",
          fontSize: 10,
          fontWeight: 700,
          color: "#8E8A84",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginTop: 4,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function formatVolume(kg) {
  if (!kg) return 0;
  if (kg >= 1000) return `${(kg / 1000).toFixed(1)}k`;
  return Math.round(kg);
}
